<template>
  <VModalWidget :dismiss="dismiss" :submit="doSubmit" :submitting="submitting" :submitTitle="this.$t('save')">
    <template slot="title">{{ $t('title') }}</template>

    <template slot="content">
      <FormErrors :show="showResourceErrors" :errors="resourceErrors" />
      <v-form>
        <v-text-field
          v-model.trim="form.display"
          :label="$t('labels.displayName')"
          :error-messages="displayErrors"
          type="email"
          required
          @input="$v.form.display.$touch()"
          @blur="$v.form.display.$touch()"
          v-on:keyup.enter="doSubmit"
        />
        <v-text-field v-if="boardItem" :value="boardItem.entity.refname" :label="$t('labels.refname')" readonly />
        <v-text-field v-model.trim="form.description" :label="$t('labels.description')" v-on:keyup.enter="doSubmit" />
        <v-text-field
          v-if="boardItem"
          :value="transformDate(boardItem.entity.created_on)"
          :label="$t('labels.createdOn')"
          readonly
        />
        <v-text-field
          v-if="boardItem"
          :value="transformDate(boardItem.entity.updated_on)"
          :label="$t('labels.updatedOn')"
          readonly
        />
      </v-form>
    </template>
  </VModalWidget>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { VModalWidget, BaseForm, FormErrors } from '@argon/app/components'
import { diffObjects, formatDT } from '@argon/app/helpers'
import { required } from 'vuelidate/lib/validators'
import { NAMESPACE } from '../store'
import { NAMESPACE as ME_NAMESPACE } from '@argon/iam/me/store'

export default {
  name: 'EntityPropertiesWidget',
  extends: BaseForm,
  components: {
    VModalWidget,
    FormErrors
  },
  props: {
    orgRef: String,
    boardRef: {
      type: [String, Number],
      required: true
    },
    entityId: {
      type: [String, Number],
      required: true
    },
    dismiss: { type: Function, required: true }
  },
  validations: {
    form: {
      display: { required },
      description: {}
    }
  },
  data: () => ({
    form: {
      display: '',
      description: ''
    },
    submitting: false
  }),
  computed: {
    ...mapState(NAMESPACE, ['boardItem']),
    ...mapGetters(ME_NAMESPACE, ['timezone']),
    proxyURL() {
      return `/boards/${this.boardRef}/items/${this.entityId}`
    },
    displayErrors() {
      const errors = []
      if (!this.$v.form.display.$dirty) return errors
      !this.$v.form.display.required && errors.push(this.$t('required'))
      this.getServerErrors('display', errors)
      return errors
    }
  },
  methods: {
    ...mapActions(NAMESPACE, ['patchEntity']),
    transformDate(value) {
      return value ? formatDT(value, { format: 'LLL', tz: this.timezone }) : ''
    },
    doSubmit() {
      this.resetServerErrors()
      if (this.$v.form.$invalid) {
        return this.$v.form.$touch()
      }
      this.submitting = true
      let data = diffObjects(this.form, this.boardItem.entity)
      let payload = { url: this.boardItem.entity.uris.self, proxy: this.proxyURL, data }
      this.patchEntity(payload)
        .then(() => {
          this.dismiss()
        })
        .catch(this.setServerErrors)
        .finally(() => {
          this.submitting = false
        })
    }
  },
  created() {
    if (this.boardItem) {
      // picking nested objects brings along their observable status
      // making using `diffObjects` impossible.
      this.form = {
        display: this.boardItem.entity.display,
        description: this.boardItem.entity.description
      }
    }
  }
}
</script>

<i18n>
{
  "en": {
    "save": "@:labels.save",
    "title": "@:labels.properties"
  },
  "fr": {
    "save": "@:labels.save",
    "title": "@:labels.properties"
  }
}
</i18n>
